import { Box, Card, CardHeader, CircularProgress, Container, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Comments from "../components/Comments/Comments";
import VideoPlayer from "../components/Player/VideoPlayer";
import VideosCard from "../components/video-card";
import {
  getChannelByID,
  getChannelVideos,
} from "../services/APIServices/channel.api";
import {
  getHomePageVideos,
  getHomePageVideosRecommendation,
  getVideoDetailsByID,
} from "../services/APIServices/videos.api";
import { useAppSelector } from "../store/hooks";
import { getUser } from "../store/reducers/user.reducer";
import { ChannelDetails } from "../types/channel";
import { VideoResponse } from "../types/medial";
import axios from "axios";
import { videoUpperLimit } from "../utils/constants";
import { ShortVideosCard } from "./Shorts/Shorts";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { isServer, safeLocalStorage } from "../utils/ssr";

interface WatchPageProps {
  initialData?: {
    videoDetails?: VideoResponse;
  };
}

export const WatchPage = ({ initialData }: WatchPageProps) => {
  let { videoID } = useParams();
  const { t } = useTranslation();
  const [videoDetails, setVideoDetails] = useState<VideoResponse | null>(
    initialData?.videoDetails || null
  );
  const [channel, setChannel] = useState<ChannelDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [suggestionVideos, setSuggestionVideos] = useState<
    VideoResponse[] | null
  >(null);
  const [channelVideos, setChannelVideos] = useState<VideoResponse[] | null>(
    null
  );
  const user = useAppSelector((state) => getUser(state));
  const navigate = useNavigate();
  const [cancelToken, setCancelToken] = useState<any>(null);
  const playerSide = useRef<any>(null);
  const [hasNext, setHasNext] = useState(false);
  const [currentLowerLimit, setCurrentLowerLimit] = useState(0);
  const [currentUpperLimit, setCurrentUpperLimit] = useState(9);

  const saveWatchedVideoId = (videoID: string) => {
    if (isServer) return;
    const watched = JSON.parse(safeLocalStorage("watchedVideos", "[]"));
    if (!watched.includes(videoID)) {
      watched.push(videoID);
      localStorage.setItem("watchedVideos", JSON.stringify(watched));
    }
  };

  useEffect(() => {
    // Skip if server provided this exact video
    if (initialData?.videoDetails?.videoID === videoID) {
      console.log('✅ [SSR] Using server-rendered video data - NO API call');
      // Fetch channel data only
      if (initialData?.videoDetails?.channelID) {
        getChannelByID(initialData.videoDetails.channelID as string, user?.userID).then(
          (res: any) => {
            setChannel(res);
          }
        );
      }
      return;
    }

    if (videoID && videoDetails?.videoID !== videoID) {
      const cancelTokenSource = axios.CancelToken.source();
      setCancelToken(cancelTokenSource);
      getVideoDetailsByID(videoID, cancelTokenSource.token).then((res: any) => {
        setVideoDetails(res);
        getChannelByID(res?.channelID as string, user?.userID).then(
          (res: any) => {
            setChannel(res);
          }
        );
      });
      if (!user?.userID && !isServer) {
        saveWatchedVideoId(videoID);
      } else if (!isServer) {
        localStorage.removeItem("watchedVideos");
      }
    } else if (!videoID) navigate(-1);

    return () => {
      setChannel(null);
      if (!initialData?.videoDetails) {
        setVideoDetails(null);
      }
      if (cancelToken) {
        cancelToken.cancel("Request cancelled");
      }
    };
  }, [videoID, initialData]);

  useEffect(() => {
    const isLoggedIn = !!user;
    let videoIdsFromUI: any;
    if (!isLoggedIn && !isServer) {
      const watched = JSON.parse(safeLocalStorage("watchedVideos", "[]"));
      if (watched.length) {
        videoIdsFromUI = watched.join(",");
      }
    }

    getHomePageVideosRecommendation({
      lowerLimit: 0,
      upperLimit: videoUpperLimit,
      userID: user?.userID,
      videoIdsFromUI: videoIdsFromUI
    })
      .then((response: any) => {
        if (response?.length >= 0) {
          // const filteredVideos = response.filter(
          //   (video: any) => video.videoType !== 'short'
          // );
          setSuggestionVideos(
            response.filter((video: any) => video.videoID !== videoID)
          );
          setHasNext(response.length >= videoUpperLimit);
          setLoading(false);
        }
      })
      .catch((err: any) => console.error(err));

    return () => {
      setSuggestionVideos([]);
    };
  }, [videoDetails]);

  useEffect(() => {
    if (channel) {
      getChannelVideos(channel?.channelID).then((res: any) => {
        setChannelVideos(res.filter((video: any) => video.videoID !== videoID));
      });
    }
  }, [channel, videoID]);

  const loadMore = () => {
    if (!loading && hasNext) {
      setLoading(true);
      const newLowerLimit = currentUpperLimit + 1;
      const newUpperLimit = newLowerLimit + videoUpperLimit - 1;

      setCurrentLowerLimit(newLowerLimit);
      setCurrentUpperLimit(newUpperLimit);
      const cancelTokenSource = axios.CancelToken.source();
      setCancelToken(cancelTokenSource);

      const isLoggedIn = !!user;
      let videoIdsFromUI: any;
      if (!isLoggedIn && !isServer) {
        const watched = JSON.parse(safeLocalStorage("watchedVideos", "[]"));
        if (watched.length) {
          videoIdsFromUI = watched.join(",");
        }
      }

      getHomePageVideosRecommendation({
        lowerLimit: newLowerLimit,
        upperLimit: newUpperLimit,
        userID: user?.userID,
        videoIdsFromUI: videoIdsFromUI
      })
        .then((response: any) => {
          if (response?.length >= 0) {

            const filteredVideos = response.filter(
              (video: any) => video.videoType !== 'short'
            );

            setSuggestionVideos((prevSuggestionVideos: any) => [
              ...prevSuggestionVideos,
              ...filteredVideos.filter((video: VideoResponse) => video.videoID !== videoID),
            ]);
            setHasNext(response?.length === videoUpperLimit);
            setLoading(false);
          }
        })
        .catch((err) => console.error(err));
      return () => {
        if (cancelTokenSource) {
          cancelTokenSource.cancel("Request cancelled");
        }
      };
    }
  };

  useEffect(() => {
    if (isServer) return;

    const handleScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 100;

      if (nearBottom && !loading && hasNext) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loading, hasNext, loadMore]);

  return (
    <Container maxWidth={false}>
      <Grid container maxWidth={"100%"} spacing={1}>
        {/* Left Side: Video Player + Comments */}
        <Grid item xs={12} sm={12} lg={8} md={6}>
          <Stack direction={"column"} spacing={2} ref={playerSide}>
            <VideoPlayer
              videoDetails={videoDetails}
              setVideoDetails={setVideoDetails}
              channel={channel}
              setChannel={setChannel}
            />
            <Comments videoID={videoID} />
          </Stack>
        </Grid>

        {/* Right Side: Channel Videos (Including Shorts) */}
        <Grid item xs={12} sm={12} lg={4} md={6}>
          <div
            style={{
              backgroundColor: "rgba(256,256,256, 0.1)",
              borderRadius: "5px",
              height: isMobile ? "auto" : playerSide?.current?.clientHeight,
              maxHeight: isMobile ? "none" : "100%",
              overflowY: isMobile ? "unset" : "scroll",
              padding: "10px",
              paddingTop: "0px",
              position: "relative",
              display: channelVideos?.length === 0 && isMobile ? "none" : "block",
            }}
          >
            {channelVideos === null ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                }}
              >
                <CircularProgress sx={{ color: "blue" }} />
              </Box>
            ) : channelVideos?.length > 0 ? (
              <>
                <Box sx={{ position: "sticky", top: 0, backgroundColor: "background.paper", zIndex: 10, p: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {/* More from {channel?.name || "this channel"} */}
                    {t("moreFrom", { channelName: channel?.name || "this channel" })}
                  </Typography>
                </Box>
                <VideosCard
                  small
                  maxWidth={12}
                  data={channelVideos?.filter((video: any) => video.videoType !== "short")}
                  isLoading={false}
                />
              </>
            ) : (
              !isMobile && (
                <Box sx={{ textAlign: "center", mt: 4, p: 2 }}>
                  <Typography variant="h5" fontWeight="bold" color="textPrimary">
                    {/* No Videos Available */}
                    {t("noVideosAvailable")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {/* This channel has not uploaded any videos yet. Check back later! */}
                    {t("noVideosYet")}
                  </Typography>
                </Box>
              )
            )}
          </div>
        </Grid>
      </Grid>

      {/* Recommended Videos (Including Shorts) */}
      {/* {suggestionVideos?.some((video: any) => video.videoType === "short") && (
        <ShortVideosCard
          data={suggestionVideos?.filter((video: any) => video.videoType === "short")}
          // onVideoClick={(videoId: any) => navigate(`/shorts/${videoId}`)}
        />
      )} */}

      <Typography variant="h6" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
        {/* Recommended for You */}
        {t("recommendedForYou")}
      </Typography>
      <VideosCard
        small
        maxWidth={4}
        data={suggestionVideos?.filter((video: any) => video.videoType !== "short")}
        isLoading={suggestionVideos === null}
        loadMore={loadMore}
      />
    </Container>

  );
};
